import { Moment } from 'moment';
import { PosicaoResponse } from 'common/genericTypes';
import { formatNumber } from 'utils/number';
import { stripCpfCnpjFormatting } from 'utils/validation';

export const MENSAGEM_QUANTIDADE_ACIMA_DO_DISPONIVEL = 'Quantidade acima do disponível para o investidor';
export const MENSAGEM_QUANTIDADE_NAO_POSITIVA = 'Quantidade deve ser maior que zero';
export const MENSAGEM_DATA_DE_RETORNO_RETROATIVA = 'A data de retorno não pode ser anterior a hoje';

export const isQuantidadeNaoPositiva = (quantidade?: number): boolean =>
  quantidade !== undefined && quantidade !== null && quantidade <= 0;

export const isQuantidadeAcimaDoDisponivel = (quantidade?: number, quantidadeDisponivel?: number): boolean => {
  if (quantidade === undefined || quantidade === null || quantidadeDisponivel === undefined) {
    return false;
  }

  return quantidade > quantidadeDisponivel;
};

export const resolverErroDaQuantidade = (quantidade?: number, quantidadeDisponivel?: number): string | undefined => {
  if (isQuantidadeNaoPositiva(quantidade)) {
    return MENSAGEM_QUANTIDADE_NAO_POSITIVA;
  }

  if (isQuantidadeAcimaDoDisponivel(quantidade, quantidadeDisponivel)) {
    return MENSAGEM_QUANTIDADE_ACIMA_DO_DISPONIVEL;
  }

  return undefined;
};

export const isDataDeRetornoRetroativa = (dataDeRetorno?: Moment | null, hoje?: Moment): boolean => {
  if (!dataDeRetorno || !hoje) {
    return false;
  }

  return dataDeRetorno.isBefore(hoje, 'day');
};

/**
 * Enquanto o instrumento não é carregado pela busca não existe saldo apurado.
 * Exibir zero nesse momento faz o usuário ler "sem saldo" onde o correto é
 * "ainda não consultado", que foi o defeito relatado.
 */
export const descreverQuantidadeDisponivel = (quantidadeDisponivel?: number): string =>
  quantidadeDisponivel === undefined
    ? ''
    : `Quantidade disponível: ${formatNumber(quantidadeDisponivel, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 8,
      })}`;

/**
 * Casa a posição por investidor E instrumento.
 *
 * Casar só por CPF/CNPJ é frágil: o endpoint de posições roteia o custodiante
 * para GetPosicoesToInteroperabilidadeDeSaida, que não honra
 * instrumentoFinanceiroId. Se o investidor tem posição em mais de um
 * instrumento, o primeiro match pode ser de outro instrumento — e esse saldo
 * alimenta a validação de quantidade, que bloqueia o usuário. Conferir o
 * instrumento aqui torna a tela correta independente do que a API filtra.
 */
export const encontrarPosicaoDoInvestidor = (
  posicoes: Array<PosicaoResponse> | undefined,
  investidorCpfCnpj: string,
  instrumentoFinanceiroGuid?: string
): PosicaoResponse | undefined => {
  const cpfCnpjProcurado = stripCpfCnpjFormatting(investidorCpfCnpj);

  if (!cpfCnpjProcurado || !instrumentoFinanceiroGuid) {
    return undefined;
  }

  return posicoes?.find(
    (posicao) =>
      stripCpfCnpjFormatting(posicao.investidor.cpfCnpj ?? '') === cpfCnpjProcurado &&
      posicao.instrumentoFinanceiro?.instrumentoFinanceiroGuid === instrumentoFinanceiroGuid
  );
};

export interface CamposDaCompromissada {
  temInstrumento: boolean;
  investidorCpfCnpjValido: boolean;
  cnpjCustodianteDestinoValido: boolean;
  custodianteEncontrado: boolean;
  contaDepositoDestinoValida: boolean;
  quantidade?: number;
  quantidadeDisponivel?: number;
  precoUnitario?: number;
  indexador?: string;
  dataDeRetorno?: Moment | null;
  hoje?: Moment;
}

export const podeComandarCompromissada = (campos: CamposDaCompromissada): boolean => {
  const preenchido =
    campos.temInstrumento &&
    campos.investidorCpfCnpjValido &&
    campos.cnpjCustodianteDestinoValido &&
    campos.custodianteEncontrado &&
    campos.contaDepositoDestinoValida &&
    !!campos.quantidade &&
    !!campos.precoUnitario &&
    !!campos.indexador &&
    !!campos.dataDeRetorno;

  if (!preenchido) {
    return false;
  }

  return (
    !resolverErroDaQuantidade(campos.quantidade, campos.quantidadeDisponivel) &&
    !isDataDeRetornoRetroativa(campos.dataDeRetorno, campos.hoje)
  );
};
